import type {
  AccountDto,
  CprContactGroup,
  CprContactGroupMember,
  CprContactGroupMemberListResponse,
  CprContactGroupMembership,
  CprContactResponse,
  CprContactResponseListResponse,
  CprFiltersResult,
  CreateCprValidationEventRequest,
  ManagementContactGroupMember,
  ManagementContactGroupMembersListResponse,
  SelectDto,
} from '../generated/model';
import { MarketRole } from '../generated/model';
import { generateEan } from './connection';

export const createCprContacts = (
  overrides: Partial<CprContactGroupMemberListResponse> = {},
): CprContactGroupMemberListResponse => ({
  items: [
    createCprContact({
      email: 'pietje.puk@eneco.nl',
      name: 'Pietje Puk',
      organisation: createOrganisation({ name: 'Eneco' }),
    }),
    createCprContact({
      email: 'jan.klein@edsn.nl',
      id: '321',
      name: 'Jan Klein',
      organisation: createOrganisation({ name: 'EDSN' }),
    }),
    ...Array(7).fill(createCprContact()),
  ],
  totalCount: 20,
  ...overrides,
});

export const createCprContact = (
  overrides: Partial<CprContactGroupMember> = {},
): CprContactGroupMember => ({
  contactGroup: createContactGroupSelect(),
  contactId: '123',
  email: 'john.doe@liander.nl',
  id: '123',
  marketParty: createOrganisation(),
  name: 'John Doe',
  organisation: createOrganisation({ name: 'Liander' }),
  phone: '06-12345678',
  ...overrides,
});

export const createContactGroupSelect = (
  overrides: Partial<SelectDto> = {},
): SelectDto => ({
  id: 'c6f7e4a2-7dc6-ea11-a816-000d3a21145f',
  label: 'Algemene groep',
  ...overrides,
});

export const createOrganisation = (
  overrides: Partial<AccountDto> = {},
): AccountDto => ({
  ean13: generateEan(13, 'marketParty'),
  id: 'c6f7e4a2-7dc6-ea11-a816-000d3a21145f',
  marketRole: MarketRole.LV,
  name: 'Test organisatie',
  ...overrides,
});

export const createLeveringszekerheidContactGroupSelect = (
  overrides: Partial<SelectDto> = {},
): SelectDto => ({
  group: 'Leveringszekerheid',
  id: '9abf46b6-c432-ea11-a813-000d3a24f3f0',
  label: 'Leveringszekerheid',
  ...overrides,
});

export const createCprFilters = (
  overrides: Partial<CprFiltersResult> = {},
): CprFiltersResult => ({
  contactGroups: [
    createContactGroupSelect(),
    createLeveringszekerheidContactGroupSelect(),
  ],
  marketParties: [
    { id: createOrganisation().id, label: createOrganisation().name },
  ],
  organisations: [{ id: createOrganisation().id, label: 'Liander' }],
  ...overrides,
});

export const createContactGroup = (
  overrides: Partial<CprContactGroup> = {},
): CprContactGroup => ({
  id: 'c6f7e4a2-7dc6-ea11-a816-000d3a21145f',
  isRequired: true,
  marketRoles: [],
  name: 'Algemene groep',
  ...overrides,
});

export const createCprManagementContact = (
  overrides: Partial<ManagementContactGroupMember> = {},
): ManagementContactGroupMember => ({
  contactGroup: createContactGroup(),
  contactId: '123',
  email: 'john.doe@liander.nl',
  id: '123',
  name: 'John Doe',
  organisation: createOrganisation(),
  phone: '06-12345678',
  ...overrides,
});

export const createCprManagementContacts = (
  overrides: Partial<ManagementContactGroupMembersListResponse> = {},
): ManagementContactGroupMembersListResponse => ({
  items: [
    ...Array(5).fill(createCprManagementContact()),
    ...Array(5).fill(
      createCprManagementContact({
        contactGroup: createContactGroup({ isRequired: false }),
      }),
    ),
  ],
  totalCount: 20,
  latestContactValidationDate: '2025-10-11T00:00:00Z',
  ...overrides,
});

export const createCprValidationEventRequest = (
  overrides: Partial<CreateCprValidationEventRequest> = {},
): CreateCprValidationEventRequest => ({
  organizationId: '1',
  validationDate: new Date().toISOString(),
  ...overrides,
});

export const createCprContactResponse = (
  overrides: Partial<CprContactResponse> = {},
): CprContactResponse => ({
  id: '37fec085-4533-f111-88b5-7ced8d3fd292',
  email: 'jdejong@dexample.com',
  firstName: 'Jan',
  fullName: 'Jan de Jong',
  lastName: 'de Jong',
  phone: '+31612341234',
  contactGroupMemberships: [createContactgroupMembership()],
  ...overrides,
});

export const createCprContactResponses = (
  overrides: Partial<CprContactResponseListResponse> = {},
): CprContactResponseListResponse => ({
  items: [
    createCprContactResponse(),
    createCprContactResponse({
      id: '6e3269eb-6254-f111-bec6-7ced8d3fd292',
      email: 'carolien.deboer@123meetbedrijf.com',
      fullName: 'Carolien de Boer',
      firstName: 'Carolien',
      lastName: 'de Boer',
      phone: '+31123456789',
      contactGroupMemberships: [createContactgroupMembership()],
    }),
    createCprContactResponse({
      id: 'c5679fa9-4f0b-f111-8407-002248a468bb',
      email: 'disputen.opensource@energie.com',
      fullName: 'Disputen OpenSource',
      firstName: 'Disputen',
      lastName: 'OpenSource',
      phone: '+31612345678',
      contactGroupMemberships: [
        createContactgroupMembership(),
        createContactgroupMembership({
          id: '0f0fcbfe-cb60-f111-a825-7ced8d729479',
          contactGroup: createContactGroup({
            id: '8e158ab0-e0c1-ed11-83ff-000d3a666059',
            name: 'Alle issues rond proces Afdracht(scontrole)',
          }),
          subunit: createSubUnit(),
        }),
        createContactgroupMembership({
          id: 'af2688a8-c35d-f111-a825-7ced8d49b514',
          contactGroup: createContactGroup({
            id: '2611f7e2-e0c1-ed11-83ff-0022489cab04',
            name: 'Beveiligingsincidenten',
            isRequired: false,
          }),
          subunit: createSubUnit({
            id: '14cc233d-31e2-ef11-a730-7c1e5234429f',
            ean13: '3051209597737',
            name: 'Flexmetal Solution',
          }),
        }),
      ],
    }),
    ...Array(7)
      .fill(null)
      .map(() => createCprContactResponse()),
  ],
  totalCount: 9,
  ...overrides,
});

export const createContactgroupMembership = (
  overrides: Partial<CprContactGroupMembership> = {},
): CprContactGroupMembership => ({
  id: '9bd2c6f8-cb60-f111-a825-7ced8d729479',
  contactGroup: createContactGroup(),
  subunit: createSubUnit(),
  ...overrides,
});

export const createSubUnit = (
  overrides: Partial<AccountDto> = {},
): AccountDto => ({
  id: '3868cbe0-2ce2-ef11-a730-000d3a654ea6',
  ean13: '5481446571135',
  name: 'De Snoeicoach',
  marketRole: 'UNKNOWN',
  ...overrides,
});

export const createCprContactGroups = (
  overrides: CprContactGroup[] = [],
): CprContactGroup[] =>
  overrides.length
    ? overrides
    : [
        createContactGroup(),
        createContactGroup({
          id: '8e158ab0-e0c1-ed11-83ff-000d3a666059',
          isRequired: false,
          name: 'Alle issues rond proces Afdracht(scontrole)',
        }),
      ];

export const createCprContactsWithGroups = (
  overrides: Partial<CprContactGroupMemberListResponse> = {},
): CprContactGroupMemberListResponse => ({
  items: [
    createCprContact({
      id: '1',
      contactId: 'contact1',
      name: 'Test Contact 1',
      email: 'test@example.com',
      contactGroup: { id: 'group1', label: 'Algemene groep' },
      organisation: {
        id: 'org1',
        name: 'Org 1',
        ean13: '123',
        marketRole: 'BRP',
        externalNmrId: 'nmr1',
      } as any,
      marketParty: {
        id: 'mp1',
        name: 'MP 1',
        ean13: '456',
        marketRole: 'BRP',
        externalNmrId: 'nmr2',
      } as any,
    }),
    createCprContact({
      id: '2',
      contactId: 'contact2',
      name: 'Test Contact 2',
      email: 'test@example.com',
      contactGroup: { id: 'group2', label: 'Zuivere teruglevering' },
      organisation: {
        id: 'org1',
        name: 'Org 1',
        ean13: '123',
        marketRole: 'BRP',
        externalNmrId: 'nmr1',
      } as any,
      marketParty: {
        id: 'mp1',
        name: 'MP 1',
        ean13: '456',
        marketRole: 'BRP',
        externalNmrId: 'nmr2',
      } as any,
    }),
  ],
  totalCount: 2,
  ...overrides,
});
